// hooks/useProgressionCached.js - Version avec cache intelligent
import { useCallback, useEffect, useState } from 'react';
import { useCacheManager } from './useCacheManager';

const API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL || 'http://localhost:5003/api';

export const useProgressionCached = (options = {}) => {
    const {
        category = null,
        includeDetails = false,
        autoRefresh = false,
        refreshInterval = 60000, // 1 minute par défaut
        cacheDuration = 5 * 60 * 1000, // 5 minutes
        forceRefresh = false
    } = options;

    const [progressions, setProgressions] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);

    // Hook de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache } = useCacheManager();

    // Headers pour les requêtes
    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
    }), []);

    // Fonction utilitaire pour gérer les erreurs
    const handleError = useCallback((error, customMessage = 'Une erreur est survenue') => {
        const message = error?.message || customMessage;
        setError(error);
        console.error('❌ Erreur progressions:', message);
        return { success: false, error: message };
    }, []);

    // 📋 CHARGER LES PROGRESSIONS AVEC CACHE
    const fetchProgressions = useCallback(async (customOptions = {}, useCache = true) => {
        // Préparer la clé de cache
        const options = { category, includeDetails, ...customOptions };
        const cacheKey = getCacheKey('progressions', options);

        // Vérifier le cache d'abord (sauf si forceRefresh)
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log('📦 Progressions chargées depuis le cache');
                setProgressions(cachedData);
                setIsFromCache(true);
                setError(null);
                return { success: true, progressions: cachedData, fromCache: true };
            }
        }

        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleError({ message: 'Token manquant' }, 'Token manquant');
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            // Construction des paramètres de requête
            const params = new URLSearchParams();
            if (options.category) params.append('category', options.category);
            if (options.includeDetails) params.append('details', 'true');

            const url = `${API_BASE_URL}/progression${params.toString() ? '?' + params.toString() : ''}`;

            const response = await fetch(url, {
                headers: getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}`);
            }

            const data = await response.json();
            const validProgressions = (data.progressions || []).filter(prog => {
                if (!prog.id_progression) {
                    return false;
                }
                return true;
            });
            
            // Organiser par catégorie pour correspondre au composant
            const progressionsByCategory = {};
            validProgressions.forEach(prog => {
                progressionsByCategory[prog.category] = prog;
            });

            // Sauvegarder dans le cache
            setCacheData(cacheKey, progressionsByCategory, cacheDuration);
            
            setProgressions(progressionsByCategory);
            setLastUpdated(new Date());
            
            console.log(`✅ ${validProgressions.length} progressions chargées depuis l'API`);
            return { success: true, progressions: progressionsByCategory, fromCache: false };
        } catch (err) {
            return handleError(err, 'Erreur lors de la récupération des progressions');
        } finally {
            setLoading(false);
        }
    }, [category, includeDetails, getCacheKey, getCacheData, setCacheData, getHeaders, handleError, forceRefresh, cacheDuration]);

    // 🎯 RÉCUPÉRER UNE PROGRESSION PAR CATÉGORIE
    const getProgressionByCategory = useCallback(async (categoryName) => {
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Token manquant');

        const validCategories = ['patrimoine', 'cashflow', 'reseau', 'maitrise'];
        if (!validCategories.includes(categoryName)) {
            throw new Error('Catégorie invalide');
        }

        // Vérifier le cache d'abord
        const cacheKey = getCacheKey('progression_category', { category: categoryName, includeDetails });
        const cachedData = getCacheData(cacheKey);
        if (cachedData && !forceRefresh) {
            console.log(`📦 Progression ${categoryName} chargée depuis le cache`);
            return cachedData;
        }

        try {
            const params = new URLSearchParams();
            if (includeDetails) params.append('details', 'true');

            const response = await fetch(`${API_BASE_URL}/progression/${categoryName}?${params.toString()}`, {
                headers: getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}`);
            }

            const data = await response.json();
            
            // Sauvegarder dans le cache
            setCacheData(cacheKey, data.progression, cacheDuration);
            
            return data.progression;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [includeDetails, getCacheKey, getCacheData, setCacheData, getHeaders, forceRefresh, cacheDuration]);

    // ⭐ METTRE À JOUR LES JALONS avec mise à jour optimiste
    const updateMilestones = useCallback(async (categoryName, milestones) => {
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Token manquant');

        if (!categoryName || !Array.isArray(milestones) || milestones.length < 3) {
            throw new Error('Catégorie et au moins 3 jalons requis');
        }

        // Validation que les jalons sont croissants
        for (let i = 1; i < milestones.length; i++) {
            if (milestones[i] <= milestones[i - 1]) {
                throw new Error('Les jalons doivent être en ordre croissant');
            }
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousProgressions = { ...progressions };
        setProgressions(prev => ({
            ...prev,
            [categoryName]: {
                ...prev[categoryName],
                milestones,
                isOptimistic: true
            }
        }));

        try {
            const response = await fetch(`${API_BASE_URL}/progression/${categoryName}/milestones`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ milestones })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache des progressions
            invalidateCache('progressions');
            invalidateCache('progression_category');
            console.log('🗑️ Cache progressions invalidé après mise à jour jalons');

            // Mettre à jour avec les vraies données du serveur
            setProgressions(prev => ({
                ...prev,
                [categoryName]: data.progression
            }));

            return { success: true, progression: data.progression };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setProgressions(previousProgressions);
            return handleError(err, 'Erreur lors de la mise à jour des jalons');
        } finally {
            setLoading(false);
        }
    }, [progressions, getHeaders, handleError, invalidateCache]);

    // 📊 METTRE À JOUR LA VALEUR ACTUELLE avec mise à jour optimiste
    const updateCurrentValue = useCallback(async (categoryName, currentValue) => {
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Token manquant');

        if (!categoryName || currentValue === undefined || currentValue < 0) {
            throw new Error('Catégorie et valeur positive requises');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousProgressions = { ...progressions };
        setProgressions(prev => ({
            ...prev,
            [categoryName]: {
                ...prev[categoryName],
                current_value: currentValue,
                isOptimistic: true
            }
        }));

        try {
            const response = await fetch(`${API_BASE_URL}/progression/${categoryName}/current-value`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ current_value: currentValue })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache des progressions
            invalidateCache('progressions');
            invalidateCache('progression_category');
            console.log('🗑️ Cache progressions invalidé après mise à jour valeur');

            // Mettre à jour avec les vraies données du serveur
            setProgressions(prev => ({
                ...prev,
                [categoryName]: data.progression
            }));

            return { success: true, progression: data.progression };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setProgressions(previousProgressions);
            return handleError(err, 'Erreur lors de la mise à jour de la valeur');
        } finally {
            setLoading(false);
        }
    }, [progressions, getHeaders, handleError, invalidateCache]);

    // 🏆 METTRE À JOUR L'INDEX DU TROPHÉE avec mise à jour optimiste
    const updateTrophyIndex = useCallback(async (categoryName, trophyIndex) => {
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Token manquant');

        if (!categoryName || trophyIndex === undefined || trophyIndex < 0) {
            throw new Error('Catégorie et index de trophée valide requis');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousProgressions = { ...progressions };
        setProgressions(prev => ({
            ...prev,
            [categoryName]: {
                ...prev[categoryName],
                current_trophy_index: trophyIndex,
                isOptimistic: true
            }
        }));

        try {
            const response = await fetch(`${API_BASE_URL}/progression/${categoryName}/trophy`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ trophy_index: trophyIndex })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache des progressions
            invalidateCache('progressions');
            invalidateCache('progression_category');
            console.log('🗑️ Cache progressions invalidé après mise à jour trophée');

            // Mettre à jour avec les vraies données du serveur
            setProgressions(prev => ({
                ...prev,
                [categoryName]: data.progression
            }));

            return { success: true, progression: data.progression };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setProgressions(previousProgressions);
            return handleError(err, 'Erreur lors de la mise à jour du trophée');
        } finally {
            setLoading(false);
        }
    }, [progressions, getHeaders, handleError, invalidateCache]);

    // 🔄 RÉINITIALISER UNE PROGRESSION avec mise à jour optimiste
    const resetProgression = useCallback(async (categoryName) => {
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Token manquant');

        if (!categoryName) {
            throw new Error('Catégorie requise');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousProgressions = { ...progressions };
        setProgressions(prev => ({
            ...prev,
            [categoryName]: {
                ...prev[categoryName],
                current_value: 0,
                current_trophy_index: 0,
                progress_percentage: 0,
                isOptimistic: true
            }
        }));

        try {
            const response = await fetch(`${API_BASE_URL}/progression/reset/${categoryName}`, {
                method: 'POST',
                headers: getHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Erreur ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache des progressions
            invalidateCache('progressions');
            invalidateCache('progression_category');
            console.log('🗑️ Cache progressions invalidé après reset');

            // Mettre à jour avec les vraies données du serveur
            setProgressions(prev => ({
                ...prev,
                [categoryName]: data.progression
            }));

            return { success: true, progression: data.progression };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setProgressions(previousProgressions);
            return handleError(err, 'Erreur lors de la réinitialisation');
        } finally {
            setLoading(false);
        }
    }, [progressions, getHeaders, handleError, invalidateCache]);

    // 🔄 RAFRAÎCHIR LES PROGRESSIONS (force le refetch)
    const refreshProgressions = useCallback(() => {
        console.log('🔄 Force refresh des progressions');
        return fetchProgressions({}, false); // false = ne pas utiliser le cache
    }, [fetchProgressions]);

    // 🎯 REFETCH avec gestion intelligente du cache
    const refetchProgressions = useCallback(async () => {
        console.log('🔄 Refetch intelligent des progressions');
        
        // Si les données sont récentes (moins de 1 minute), ne pas refetch
        if (lastUpdated && (Date.now() - lastUpdated.getTime()) < 60000 && !forceRefresh) {
            console.log('⏭️ Données récentes, skip refetch');
            return { success: true, progressions, fromCache: true };
        }
        
        return fetchProgressions({}, false);
    }, [fetchProgressions, lastUpdated, forceRefresh, progressions]);

    // Fonctions utilitaires pour correspondre au composant original
    const formatValue = useCallback((category, value) => {
        if (!value) return '0';
        
        const numValue = parseFloat(value);
        const numberFormatOptions = {
            style: 'decimal',
            maximumFractionDigits: 0
        };

        if (category === 'patrimoine') {
            if (numValue >= 1000) {
                return `${new Intl.NumberFormat('fr-FR', numberFormatOptions).format(numValue / 1000)}K€`;
            }
            return `${new Intl.NumberFormat('fr-FR', numberFormatOptions).format(numValue)}€`;
        }

        if (category === 'cashflow') {
            return `${new Intl.NumberFormat('fr-FR', numberFormatOptions).format(numValue)}€`;
        }

        return new Intl.NumberFormat('fr-FR', numberFormatOptions).format(numValue);
    }, []);

    const getProgressText = useCallback((categoryName) => {
        const prog = progressions[categoryName];
        if (!prog || !prog.milestones) return '';

        const currentIndex = prog.current_trophy_index || 0;
        const milestones = Array.isArray(prog.milestones) ? prog.milestones : JSON.parse(prog.milestones);
        const currentValue = milestones[currentIndex];
        const maxValue = milestones[milestones.length - 1];

        if (categoryName === 'reseau') {
            return `${formatValue(categoryName, currentValue)} / ${formatValue(categoryName, maxValue)} contacts`;
        }

        if (categoryName === 'maitrise') {
            return `${formatValue(categoryName, currentValue)} / ${formatValue(categoryName, maxValue)} opérations`;
        }

        return `${formatValue(categoryName, currentValue)} / ${formatValue(categoryName, maxValue)}`;
    }, [progressions, formatValue]);

    const getTopPercentileTag = useCallback((categoryName) => {
        const prog = progressions[categoryName];
        if (!prog || !prog.national_percentile) {
            return { topPercent: 50, medalColor: 'text-gray-500', bgColor: 'bg-gray-100' };
        }

        const topPercent = Math.max(0, 100 - parseFloat(prog.national_percentile));

        let medalColor = 'text-gray-500';
        let bgColor = 'bg-gray-100';

        if (topPercent <= 1) {
            medalColor = 'text-yellow-500';
            bgColor = 'bg-yellow-50';
        } else if (topPercent <= 5) {
            medalColor = 'text-gray-400';
            bgColor = 'bg-gray-50';
        } else if (topPercent <= 10) {
            medalColor = 'text-amber-600';
            bgColor = 'bg-amber-50';
        }

        return { topPercent, medalColor, bgColor };
    }, [progressions]);

    // Convertir les progressions au format attendu par le composant (milestones et currentTrophyIndices)
    const getMilestonesForComponent = useCallback(() => {
        const milestones = {};
        Object.keys(progressions).forEach(category => {
            const prog = progressions[category];
            if (prog && prog.milestones) {
                milestones[category] = Array.isArray(prog.milestones) 
                    ? prog.milestones 
                    : JSON.parse(prog.milestones);
            }
        });
        return milestones;
    }, [progressions]);

    const getCurrentTrophyIndices = useCallback(() => {
        const indices = {};
        Object.keys(progressions).forEach(category => {
            const prog = progressions[category];
            if (prog) {
                indices[category] = prog.current_trophy_index || 0;
            }
        });
        return indices;
    }, [progressions]);

    // 🔄 CHARGER LES PROGRESSIONS AU MONTAGE
    useEffect(() => {
        if (autoRefresh) {
            fetchProgressions();
        }
    }, [fetchProgressions, autoRefresh]);

    // 🔄 AUTO-REFRESH OPTIONNEL
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!autoRefresh || !token || refreshInterval <= 0) return;

        const interval = setInterval(() => {
            fetchProgressions({}, false); // Force refresh périodique
        }, refreshInterval);
        
        return () => clearInterval(interval);
    }, [autoRefresh, refreshInterval, fetchProgressions]);

    // 🧹 NETTOYER LES ERREURS
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // 📊 CALCULS DÉRIVÉS POUR LES STATISTIQUES LOCALES
    const stats = {
        totalCategories: Object.keys(progressions).length,
        completedCategories: Object.values(progressions).filter(p => p.progress_percentage >= 100).length,
        averageProgress: Object.keys(progressions).length > 0
            ? Object.values(progressions).reduce((sum, p) => sum + (p.progress_percentage || 0), 0) / Object.keys(progressions).length
            : 0,
        topPerformer: Object.keys(progressions).reduce((best, category) => {
            const prog = progressions[category];
            if (!prog) return best;
            if (!best.category || prog.progress_percentage > best.progress) {
                return { category, progress: prog.progress_percentage || 0 };
            }
            return best;
        }, { category: null, progress: 0 }),
        totalMedals: {
            gold: Object.values(progressions).filter(p => {
                const topPercent = Math.max(0, 100 - (parseFloat(p.national_percentile) || 0));
                return topPercent <= 1;
            }).length,
            silver: Object.values(progressions).filter(p => {
                const topPercent = Math.max(0, 100 - (parseFloat(p.national_percentile) || 0));
                return topPercent > 1 && topPercent <= 5;
            }).length,
            bronze: Object.values(progressions).filter(p => {
                const topPercent = Math.max(0, 100 - (parseFloat(p.national_percentile) || 0));
                return topPercent > 5 && topPercent <= 10;
            }).length
        }
    };

    return {
        // Données principales
        progressions,
        stats,
        lastUpdated,

        // États
        loading,
        error,
        isFromCache,

        // Actions principales avec mise à jour optimiste
        updateMilestones,
        updateCurrentValue,
        updateTrophyIndex,
        resetProgression,
        getProgressionByCategory,

        // Actions de rafraîchissement
        refresh: fetchProgressions,
        refreshProgressions,
        refetchProgressions,

        // Fonctions utilitaires pour le composant
        formatValue,
        getProgressText,
        getTopPercentileTag,
        getMilestonesForComponent,
        getCurrentTrophyIndices,

        // Utilitaires
        clearError,
        
        // Cache management
        invalidateCache: () => {
            invalidateCache('progressions');
            invalidateCache('progression_category');
        }
    };
};

// Hook spécialisé pour les statistiques de progression avec cache
export const useProgressionStatsCached = (options = {}) => {
    const {
        cacheDuration = 10 * 60 * 1000, // 10 minutes pour les stats
        forceRefresh = false
    } = options;

    const [userStats, setUserStats] = useState(null);
    const [nationalStats, setNationalStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);

    // Hook de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache } = useCacheManager();

    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
    }), []);

    const fetchUserStats = useCallback(async (useCache = true) => {
        const cacheKey = getCacheKey('progression_user_stats');

        // Vérifier le cache d'abord
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log('📦 Stats utilisateur chargées depuis le cache');
                setUserStats(cachedData);
                setIsFromCache(true);
                setError(null);
                return;
            }
        }

        const token = localStorage.getItem('access_token');
        if (!token) {
            setError('Token manquant');
            return;
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            const response = await fetch(`${API_BASE_URL}/progression/stats`, {
                headers: getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}`);
            }

            const data = await response.json();
            const stats = data.user_stats || {};

            // Sauvegarder dans le cache
            setCacheData(cacheKey, stats, cacheDuration);
            
            setUserStats(stats);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [getCacheKey, getCacheData, setCacheData, getHeaders, forceRefresh, cacheDuration]);

    const fetchNationalStats = useCallback(async (category = null, useCache = true) => {
        const cacheKey = getCacheKey('progression_national_stats', { category });

        // Vérifier le cache d'abord
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log('📦 Stats nationales chargées depuis le cache');
                setNationalStats(cachedData);
                setIsFromCache(true);
                setError(null);
                return;
            }
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            const params = category ? `?category=${category}` : '';
            const response = await fetch(`${API_BASE_URL}/progression/national-stats${params}`, {
                headers: getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}`);
            }

            const data = await response.json();
            const stats = data.national_stats || data.category_stats || {};

            // Sauvegarder dans le cache
            setCacheData(cacheKey, stats, cacheDuration);
            
            setNationalStats(stats);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [getCacheKey, getCacheData, setCacheData, getHeaders, forceRefresh, cacheDuration]);

    useEffect(() => {
        fetchUserStats();
        fetchNationalStats();
    }, [fetchUserStats, fetchNationalStats]);

    return {
        userStats,
        nationalStats,
        loading,
        error,
        isFromCache,
        refreshUserStats: () => fetchUserStats(false),
        refreshNationalStats: (category) => fetchNationalStats(category, false),
        clearError: () => setError(null),
        invalidateCache: () => {
            invalidateCache('progression_user_stats');
            invalidateCache('progression_national_stats');
        }
    };
};

// Hook pour une catégorie spécifique avec cache et auto-refresh
export const useProgressionCategoryCached = (category, options = {}) => {
    const { 
        autoRefresh = false, 
        refreshInterval = 30000,
        cacheDuration = 5 * 60 * 1000, // 5 minutes
        forceRefresh = false
    } = options;

    const [progression, setProgression] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);

    // Hook de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache } = useCacheManager();

    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
    }), []);

    const fetchProgression = useCallback(async (useCache = true) => {
        const token = localStorage.getItem('access_token');
        if (!token || !category) {
            if (!token) setError('Token manquant');
            return;
        }

        const validCategories = ['patrimoine', 'cashflow', 'reseau', 'maitrise'];
        if (!validCategories.includes(category)) {
            setError('Catégorie invalide');
            return;
        }

        const cacheKey = getCacheKey('progression_category_detail', { category });

        // Vérifier le cache d'abord
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log(`📦 Progression ${category} chargée depuis le cache`);
                setProgression(cachedData);
                setIsFromCache(true);
                setError(null);
                return;
            }
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            const response = await fetch(`${API_BASE_URL}/progression/${category}?details=true`, {
                headers: getHeaders()
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}`);
            }

            const data = await response.json();
            
            // Sauvegarder dans le cache
            setCacheData(cacheKey, data.progression, cacheDuration);
            
            setProgression(data.progression);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [category, getCacheKey, getCacheData, setCacheData, getHeaders, forceRefresh, cacheDuration]);

    useEffect(() => {
        fetchProgression();
    }, [fetchProgression]);

    // Auto-refresh optionnel
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!autoRefresh || !token || !category) return;

        const interval = setInterval(() => fetchProgression(false), refreshInterval);
        return () => clearInterval(interval);
    }, [autoRefresh, refreshInterval, fetchProgression, category]);

    return {
        progression,
        loading,
        error,
        isFromCache,
        refresh: () => fetchProgression(false),
        clearError: () => setError(null),
        invalidateCache: () => invalidateCache('progression_category_detail')
    };
};